import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./stage2.css";
import { fireGameAlert } from "./gameAlert";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";
import {
  MAZE_FINISH,
  MAZE_ITEMS,
  MAZE_SIZE,
  MAZE_WALLS,
  PLAYER_SIZE,
  PLAYER_SPEED,
  PLAYER_START,
  POINTS_PER_ITEM,
} from "./mazeData";
import {
  getNewPosition,
  itemRect,
  playerRect,
  toRect,
  touches,
} from "./mazeEngine";

import stage2BgDesk from "./img/riddlebgdesk.png";
import stage2BgMob from "./img/riddlebgmob.png";
import title2Img from "./img/title2.png";
import mazeLeftImg from "./img/mazeleftimg.png";
import mazeImg from "./img/maze.png";
import manImg from "./img/man.png";
import finishImg from "./img/dhanush.png";
import chilliImg from "./img/Chilli.png";
import corianderImg from "./img/Corianderleaves.png";
import gheeImg from "./img/Ghee.png";
import groundnutsImg from "./img/Groundnuts.png";
import potatoImg from "./img/Potato.png";
import sagoImg from "./img/SagoPearl.png";
import saltSugarImg from "./img/SaltSugar.png";
import stoveImg from "./img/Stove.png";
import lemonImg from "./img/LemonJuice.png";

const STAGE_NUM = 2;

const ITEM_IMAGES = {
  coin11: chilliImg,
  coin1: corianderImg,
  coin2: gheeImg,
  coin3: groundnutsImg,
  coin4: potatoImg,
  coin5: sagoImg,
  coin6: saltSugarImg,
  coin7: stoveImg,
  coin8: lemonImg,
};

const WALL_RECTS = MAZE_WALLS.map((wall) =>
  toRect(wall.left, wall.top, wall.width, wall.height)
);

const FINISH_RECT = toRect(
  MAZE_FINISH.left,
  MAZE_FINISH.top,
  MAZE_FINISH.width,
  MAZE_FINISH.height
);

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function keyFromEvent(event) {
  const key = String(event.key || "").toLowerCase();
  if (key === "arrowup" || key === "w") return "up";
  if (key === "arrowdown" || key === "s") return "down";
  if (key === "arrowleft" || key === "a") return "left";
  if (key === "arrowright" || key === "d") return "right";
  return null;
}

export default function Stage2() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage2Points, setStage2Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [collectedIds, setCollectedIds] = useState(() => new Set());
  const [reachedFinish, setReachedFinish] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mazeScale, setMazeScale] = useState(0.9);

  const mazeOuterRef = useRef(null);
  const playerElRef = useRef(null);
  const playerPosRef = useRef({ ...PLAYER_START });
  const keysRef = useRef({ up: false, down: false, left: false, right: false });
  const collectedRef = useRef(new Set());
  const reachedFinishRef = useRef(false);
  const pausedRef = useRef(false);
  const collectingRef = useRef(false);
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };

    const applyBackground = () => {
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      body.classList.remove("common-bg");
      body.style.backgroundImage = `url("${isMobile ? stage2BgMob : stage2BgDesk}")`;
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundPosition = "center center";
      body.style.backgroundSize = "100% 100%";
      body.style.backgroundAttachment = isMobile ? "initial" : "fixed";
    };

    html.classList.add("stage2-lock");
    body.classList.add("recipe-maze-page");
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    applyBackground();
    window.addEventListener("resize", applyBackground);
    window.addEventListener("orientationchange", applyBackground);

    return () => {
      html.classList.remove("stage2-lock");
      body.classList.remove("recipe-maze-page");
      window.removeEventListener("resize", applyBackground);
      window.removeEventListener("orientationchange", applyBackground);
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, []);

  useEffect(() => {
    const outer = mazeOuterRef.current;
    if (!outer) return undefined;

    const updateScale = () => {
      const w = outer.clientWidth;
      const h = outer.clientHeight;
      if (!w) return;
      // Portrait scroll layout sizes the outer box explicitly; if height is still
      // settling (0), fall back to width so the maze never collapses.
      // Integer px avoids subpixel blur when the maze bitmap fills the frame.
      const size = Math.floor(h > 0 ? Math.min(w, h) : w);
      setMazeScale(Math.min(1, size / MAZE_SIZE));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(outer);
    return () => observer.disconnect();
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage2Points);
  }, [stage2Points]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return undefined;

    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage2 = Number(st.stage2) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage2);
          setStage2Points(dbStage2);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage2 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const finishStage = useCallback(
    async (skip = false) => {
      if (submitting || submittedRef.current) return;
      if (!skip && !reachedFinishRef.current) {
        fireGameAlert({
          icon: "error",
          title: "Please complete game",
          text: "Reach the end of the maze before continuing.",
        });
        return;
      }

      submittedRef.current = true;
      setSubmitting(true);

      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stage2Score = skip ? 0 : stage2Points;
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + stage2Score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: STAGE_NUM,
          score: stage2Score,
          time: timeStr,
          total_score: finalTotal,
        });
        if (saveResult.total_score != null) {
          setDisplayTotalScore(saveResult.total_score);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
      navigate("/stage3");
    },
    [
      submitting,
      stage2Points,
      storedUser,
      isDemoBypass,
      backendBase,
      adminToken,
      stageRowId,
      navigate,
    ]
  );

  const collectItem = useCallback(
    async (item) => {
      if (collectedRef.current.has(item.id) || collectingRef.current) return;
      collectingRef.current = true;
      pausedRef.current = true;
      // Drop held movement keys so a leftover keyup/repeat cannot dismiss the alert.
      keysRef.current = { up: false, down: false, left: false, right: false };
      collectedRef.current.add(item.id);
      setCollectedIds(new Set(collectedRef.current));
      setStage2Points((p) => p + POINTS_PER_ITEM);

      try {
        const isFirstItem = collectedRef.current.size === 1;
        if (
          isDemoBypass &&
          isFirstItem &&
          !demoRedirectTriggeredRef.current
        ) {
          demoRedirectTriggeredRef.current = true;
          await fireGameAlert({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
            dismissGuardMs: 400,
          });
          window.location.assign("https://extramileplay.com/plans");
          return;
        }

        await fireGameAlert({
          icon: "success",
          text: item.message,
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
          dismissGuardMs: 400,
        });
      } finally {
        pausedRef.current = false;
        collectingRef.current = false;
      }
    },
    [isDemoBypass]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const dir = keyFromEvent(event);
      if (!dir) return;
      event.preventDefault();
      keysRef.current[dir] = true;
    };
    const onKeyUp = (event) => {
      const dir = keyFromEvent(event);
      if (!dir) return;
      event.preventDefault();
      keysRef.current[dir] = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  useEffect(() => {
    if (!dbReady) return undefined;

    let rafId = 0;
    let last = performance.now();

    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      if (!pausedRef.current && !submittedRef.current) {
        const keys = keysRef.current;
        let deltaX = 0;
        let deltaY = 0;
        if (keys.left) deltaX -= 1;
        if (keys.right) deltaX += 1;
        if (keys.up) deltaY -= 1;
        if (keys.down) deltaY += 1;

        if (deltaX || deltaY) {
          const step = PLAYER_SPEED * dt;
          const len = Math.hypot(deltaX, deltaY) || 1;
          const next = getNewPosition(
            playerPosRef.current,
            (deltaX / len) * step,
            (deltaY / len) * step,
            WALL_RECTS,
            PLAYER_SIZE
          );
          if (next) {
            playerPosRef.current = next;
            if (playerElRef.current) {
              playerElRef.current.style.left = `${(next.left / MAZE_SIZE) * 100}%`;
              playerElRef.current.style.top = `${(next.top / MAZE_SIZE) * 100}%`;
            }

            const pRect = playerRect(next, PLAYER_SIZE);
            if (!collectingRef.current) {
              for (const item of MAZE_ITEMS) {
                if (collectedRef.current.has(item.id)) continue;
                if (touches(pRect, itemRect(item))) {
                  void collectItem(item);
                  break;
                }
              }
            }

            if (!reachedFinishRef.current && touches(pRect, FINISH_RECT)) {
              reachedFinishRef.current = true;
              setReachedFinish(true);
            }
          }
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [dbReady, collectItem]);

  const setPad = useCallback((dir, pressed) => {
    keysRef.current[dir] = pressed;
  }, []);

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  if (!dbReady) {
    return <div className="stage2-loading">Loading game…</div>;
  }

  const clock = formatSecondsToClock(elapsedSec);

  return (
    <div className="stage2-page">
      <div className="stage2-header">
        <img src={title2Img} alt="The Recipe Maze" className="stage2-title-img" />
        <p className="stage2-instructions">
          Make the right choices by identifying sustainable Navratri recipes that
          minimize food waste and promote eco-conscious cooking through this maze
          experience.
        </p>
      </div>

      <div className="stage2-main">
        <div className="stage2-left">
          <div className="stage2-chip" aria-label={`Time ${clock}`}>
            Time: {clock}
          </div>
          <img src={mazeLeftImg} alt="" className="stage2-left-art" />
        </div>

        <div className="stage2-right">
          <div className="stage2-right-hud">
            <div
              className="stage2-chip"
              aria-label={`Points ${displayTotalScore}`}
            >
              Points: {displayTotalScore}
            </div>
            <button
              type="button"
              className="stage2-skip"
              disabled={submitting}
              onClick={handleSkip}
            >
              Skip
            </button>
          </div>

          <div className="stage2-maze-outer" ref={mazeOuterRef}>
            <div
              className="stage2-maze-frame"
              style={{
                width: Math.max(1, Math.round(MAZE_SIZE * mazeScale)),
                height: Math.max(1, Math.round(MAZE_SIZE * mazeScale)),
              }}
            >
            <div
              className="stage2-game"
              style={{
                backgroundImage: `url("${mazeImg}")`,
              }}
            >
              {WALL_RECTS.map((wall, i) => (
                <div
                  key={`wall-${i}`}
                  className="stage2-wall"
                  style={{
                    left: `${(wall.left / MAZE_SIZE) * 100}%`,
                    top: `${(wall.top / MAZE_SIZE) * 100}%`,
                    width: `${((wall.right - wall.left) / MAZE_SIZE) * 100}%`,
                    height: `${((wall.bottom - wall.top) / MAZE_SIZE) * 100}%`,
                  }}
                />
              ))}

              {MAZE_ITEMS.map((item) =>
                collectedIds.has(item.id) ? null : (
                  <img
                    key={item.id}
                    src={ITEM_IMAGES[item.id]}
                    alt=""
                    className="stage2-item"
                    style={{
                      left: `${item.leftPct}%`,
                      top: `${item.topPct}%`,
                      width: `${(item.width / MAZE_SIZE) * 100}%`,
                      height: `${(item.height / MAZE_SIZE) * 100}%`,
                    }}
                  />
                )
              )}

              {!reachedFinish ? (
                <img
                  src={finishImg}
                  alt="Finish"
                  className="stage2-finish"
                  style={{
                    left: `${(MAZE_FINISH.left / MAZE_SIZE) * 100}%`,
                    top: `${(MAZE_FINISH.top / MAZE_SIZE) * 100}%`,
                    width: `${(MAZE_FINISH.width / MAZE_SIZE) * 100}%`,
                    height: `${(MAZE_FINISH.height / MAZE_SIZE) * 100}%`,
                  }}
                />
              ) : null}

              <img
                ref={playerElRef}
                src={manImg}
                alt=""
                className="stage2-player"
                style={{
                  left: `${(PLAYER_START.left / MAZE_SIZE) * 100}%`,
                  top: `${(PLAYER_START.top / MAZE_SIZE) * 100}%`,
                  width: `${(PLAYER_SIZE / MAZE_SIZE) * 100}%`,
                  height: `${(PLAYER_SIZE / MAZE_SIZE) * 100}%`,
                }}
              />
            </div>
            </div>
          </div>
        </div>
      </div>

      <div className="stage2-dpad" aria-hidden="false">
        <button
          type="button"
          className="stage2-dpad__btn stage2-dpad__up"
          aria-label="Move up"
          onPointerDown={(e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            setPad("up", true);
          }}
          onPointerUp={() => setPad("up", false)}
          onPointerLeave={() => setPad("up", false)}
          onPointerCancel={() => setPad("up", false)}
        />
        <button
          type="button"
          className="stage2-dpad__btn stage2-dpad__left"
          aria-label="Move left"
          onPointerDown={(e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            setPad("left", true);
          }}
          onPointerUp={() => setPad("left", false)}
          onPointerLeave={() => setPad("left", false)}
          onPointerCancel={() => setPad("left", false)}
        />
        <button
          type="button"
          className="stage2-dpad__btn stage2-dpad__right"
          aria-label="Move right"
          onPointerDown={(e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            setPad("right", true);
          }}
          onPointerUp={() => setPad("right", false)}
          onPointerLeave={() => setPad("right", false)}
          onPointerCancel={() => setPad("right", false)}
        />
        <button
          type="button"
          className="stage2-dpad__btn stage2-dpad__down"
          aria-label="Move down"
          onPointerDown={(e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            setPad("down", true);
          }}
          onPointerUp={() => setPad("down", false)}
          onPointerLeave={() => setPad("down", false)}
          onPointerCancel={() => setPad("down", false)}
        />
      </div>

      <div className="stage2-next-wrap">
        <button
          type="button"
          className={`stage2-next${reachedFinish ? " is-ready" : ""}`}
          disabled={submitting}
          onClick={() => finishStage(false)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
